import { Box, Group, Text, Tooltip, ScrollArea, Stack } from '@mantine/core'
import type { Incident } from '@/api/topology'

interface IncidentsTimelineProps {
  incidents: Incident[]
  days?: number
}

function getDaysBetween(start: Date, end: Date): string[] {
  const days: string[] = []
  const current = new Date(start)
  while (current <= end) {
    days.push(current.toISOString().split('T')[0])
    current.setDate(current.getDate() + 1)
  }
  return days
}

const severityColor: Record<string, string> = {
  critical: 'var(--mantine-color-red-6)',
  warning: 'var(--mantine-color-yellow-6)',
}

export function IncidentsTimeline({ incidents, days = 30 }: IncidentsTimelineProps) {
  const end = new Date()
  const start = new Date()
  start.setDate(end.getDate() - days + 1)
  const allDays = getDaysBetween(start, end)

  // Group incidents by day
  const byDay: Record<string, Incident[]> = {}
  for (const inc of incidents) {
    const day = inc.date.split('T')[0]
    if (!byDay[day]) byDay[day] = []
    byDay[day].push(inc)
  }

  return (
    <Box>
      <Text size="sm" fw={600} mb="xs">Incidentes — Últimos {days} dias</Text>
      <ScrollArea type="auto">
        <Group gap={2} wrap="nowrap" style={{ minWidth: days * 20 }}>
          {allDays.map((day) => {
            const dayIncidents = byDay[day] || []
            const hasCritical = dayIncidents.some((i) => i.severity === 'critical')
            const hasWarning = dayIncidents.some((i) => i.severity === 'warning')
            const color = hasCritical
              ? severityColor.critical
              : hasWarning
                ? severityColor.warning
                : 'var(--mantine-color-teal-2)'

            return (
              <Tooltip
                key={day}
                label={
                  <Stack gap={2}>
                    <Text size="xs" fw={600}>{day}</Text>
                    {dayIncidents.length > 0
                      ? dayIncidents.map((i, idx) => (
                          <Text key={idx} size="xs">{i.alarm_name}</Text>
                        ))
                      : <Text size="xs" c="dimmed">Sem incidentes</Text>
                    }
                  </Stack>
                }
                multiline
                w={220}
              >
                <Box
                  style={{
                    width: 16,
                    height: 24,
                    borderRadius: 3,
                    backgroundColor: color,
                    cursor: 'pointer',
                    opacity: dayIncidents.length > 0 ? 1 : 0.4,
                  }}
                />
              </Tooltip>
            )
          })}
        </Group>
      </ScrollArea>
      <Group justify="space-between" mt={4}>
        <Text size="xs" c="dimmed">{allDays[0]}</Text>
        <Text size="xs" c="dimmed">Hoje</Text>
      </Group>
    </Box>
  )
}
